import { Injectable } from '@angular/core';
import { Observable, of, zip } from 'rxjs';
import { defaultIfEmpty, map, switchMap } from 'rxjs/operators';

import { AdvisoryModel, ClientSignatureType, IdInterface, OrderSignedInterface, TestInterface, TestResultInterface } from '../data';
import { ContractCacheRepository } from '../repositories/contract-cache.repository';
import { FullClientCacheRepository } from '../repositories/full-client-cache.repository';
import { TestApiRepository } from '../repositories/test-api.repository';
import { Transaction } from '../transaction/transaction';
import { UserCacheRepository } from '../repositories/user-cache.repository';

@Injectable()
export class TestProvider {
    constructor(
        private contractCacheRepository: ContractCacheRepository,
        private fullClientCacheRepository: FullClientCacheRepository,
        private userCacheRepository: UserCacheRepository,
        private testApiRepository: TestApiRepository
    ) {}

    find(clientId: IdInterface, advisoryModel: AdvisoryModel): Observable<Transaction<TestInterface>> {
        return this.testApiRepository.load(clientId, advisoryModel);
    }

    sign(clientCode: IdInterface, signatureType: ClientSignatureType): Observable<Transaction<void>> {
        return this.testApiRepository.sign(clientCode, signatureType);
    }

    continue(clientCode: IdInterface): Observable<Transaction<OrderSignedInterface>> {
        return this.testApiRepository.continue(clientCode);
    }

    acceptSignatories(clientCode: IdInterface): Observable<Transaction<void>> {
        return this.testApiRepository.acceptSignatories(clientCode);
    }

    documents(clientCode: IdInterface): Observable<Transaction<any>> {
        return this.testApiRepository.documents(clientCode);
    }

    results(clientId: IdInterface, advisoryModel: AdvisoryModel): Observable<Transaction<TestResultInterface>> {
        return this.testApiRepository.results(clientId, advisoryModel);
    }

    update(clientId: IdInterface, test: TestInterface, requiredSignature: boolean): Observable<Transaction<void>> {
        return this.testApiRepository.update(clientId, test, requiredSignature).pipe(
            switchMap((transaction) =>
                transaction.map(
                    (testResult) => {
                        const clientCacheId = this.fullClientCacheRepository.getKey(testResult.clientCode);
                        return zip(
                            this.userCacheRepository.clear().pipe(defaultIfEmpty()),
                            this.contractCacheRepository.load().pipe(
                                switchMap((contractTransaction) =>
                                    contractTransaction.map<Observable<void>>(
                                        (contract) => {
                                            [
                                                ...contract.holders.filter((it) => it.id.equals(clientId)),
                                                ...contract.authorized.filter((it) => it.id.equals(clientId)),
                                                ...contract.legalGuardians.filter((it) => it.id.equals(clientId)),
                                            ].forEach((client) => {
                                                client.mifidCompetenceId = testResult.mifidCompetenceId;
                                                client.mifidCompetenceName = testResult.mifidCompetenceName;
                                                client.profileId = testResult.profileId;
                                                client.profileName = testResult.profileName;
                                            });
                                            return this.contractCacheRepository.save(contract);
                                        },
                                        () => of()
                                    )
                                ),
                                defaultIfEmpty()
                            ),
                            this.fullClientCacheRepository.load(clientCacheId).pipe(
                                switchMap((clientTransaction) =>
                                    clientTransaction.map<Observable<void>>(
                                        (client) => {
                                            client.mifidCompetenceId = testResult.mifidCompetenceId;
                                            client.mifidCompetenceName = testResult.mifidCompetenceName;
                                            client.profileId = testResult.profileId;
                                            client.profileName = testResult.profileName;
                                            return this.fullClientCacheRepository.save(clientCacheId, client);
                                        },
                                        () => of()
                                    )
                                ),
                                defaultIfEmpty()
                            )
                        ).pipe(map(() => transaction));
                    },
                    () => of(transaction)
                )
            ),
            switchMap((transaction) =>
                transaction.map(
                    () => of(Transaction.success() as Transaction<void>),
                    () => of(Transaction.error(transaction.error) as Transaction<void>)
                )
            )
        );
    }

    upload(clientCode: IdInterface, document: File): Observable<Transaction<void>> {
        return this.testApiRepository.upload(clientCode, document);
    }
}
